import { DateTime } from 'luxon';

import type { Timestep } from '../../utils/date';
import { PRIMARY_RANGE_ID } from '../report-configurations/reportConfigurationsTypes';
import { formatSpan } from '../report-configurations/utils/comparisonFormat';
import {
  type ComparisonChartRange,
  type ComparisonPeriodTotals,
  toComparisonPeriodTotals,
} from './comparisonChartModel';
import {
  type ComparisonRangeHeader,
  getComparisonRangeHeaders,
  getPeriodColumnGroups,
  isChartableComparisonColumn,
  isComparisonReport,
} from './comparisonColumnModel';
import type { Report, ReportColumn } from './generatedReportsTypes';

export type ComparisonPeriod = {
  id: string;
  label: string;
  startOn?: string;
  endOn?: string;
  comparisonLabel?: string;
  comparisonStartOn?: string;
  comparisonEndOn?: string;
};

export type ComparisonOverTimeSeries = {
  rangeId: string;
  label: string;
  isPrimary: boolean;
  values: Array<number | null>;
};

export type ComparisonOverTimeData = {
  isComparison: boolean;
  timestep?: Timestep;
  periods: ComparisonPeriod[];
  /** Non-cumulative totals for each aligned period, used by the overview grouped-bar chart. */
  periodSeries: ComparisonOverTimeSeries[];
  /** Running totals across the full report range, used by the cumulative line chart. */
  series: ComparisonOverTimeSeries[];
};

export const EMPTY_COMPARISON_OVER_TIME_DATA: ComparisonOverTimeData = {
  isComparison: false,
  periods: [],
  periodSeries: [],
  series: [],
};

/** A chart needs both ranges and at least one real plotted value; allocated all-null series are empty. */
export const hasComparisonOverTimeValues = (series: readonly ComparisonOverTimeSeries[]): boolean =>
  series.length >= 2 &&
  series.some((item) => item.values.some((value) => typeof value === 'number' && Number.isFinite(value)));

type ComparisonSeriesRange = {
  end?: DateTime;
  start?: DateTime;
};

const validDate = (value: string | undefined): DateTime | undefined => {
  if (!value) return undefined;
  const date = DateTime.fromISO(value);
  return date.isValid ? date : undefined;
};

const getSeriesRange = (
  data: ComparisonOverTimeData,
  series: ComparisonOverTimeSeries,
  comparisonIndex: number,
): ComparisonSeriesRange => {
  const datedPeriods = data.periods.filter((period) =>
    series.isPrimary
      ? Boolean(period.startOn || period.endOn)
      : Boolean(period.comparisonStartOn || period.comparisonEndOn),
  );
  if (datedPeriods.length === 0 || (!series.isPrimary && comparisonIndex > 0)) return {};

  const first = datedPeriods[0];
  const last = datedPeriods[datedPeriods.length - 1];
  return series.isPrimary
    ? { start: validDate(first?.startOn), end: validDate(last?.endOn ?? last?.startOn) }
    : {
        start: validDate(first?.comparisonStartOn),
        end: validDate(last?.comparisonEndOn ?? last?.comparisonStartOn),
      };
};

const compactRangeLabel = ({ start, end }: ComparisonSeriesRange): string | undefined => {
  if (!start || !end) return undefined;
  if (start.hasSame(end, 'month')) return start.toFormat('LLLL');
  if (start.hasSame(end, 'quarter')) return `Q${start.quarter}`;
  if (start.hasSame(end, 'year') && start.month === 1 && end.month === 12) return start.toFormat('yyyy');
  if (start.hasSame(end, 'year')) return `${start.toFormat('LLL')} – ${end.toFormat('LLL')}`;
  return `${start.toFormat('LLL yyyy')} – ${end.toFormat('LLL yyyy')}`;
};

/** Compact labels used by the cumulative chart legend (for example, July / August or Q1 2025 / Q1 2026). */
export const getComparisonSeriesDisplayLabels = (data: ComparisonOverTimeData): Record<string, string> => {
  let comparisonIndex = 0;
  const ranges = data.series.map((series) => {
    const range = getSeriesRange(data, series, series.isPrimary ? 0 : comparisonIndex);
    if (!series.isPrimary) comparisonIndex += 1;
    return range;
  });
  const labels = data.series.map((series, index) => compactRangeLabel(ranges[index] ?? {}) ?? series.label);
  const duplicateLabels = new Set(labels.filter((label, index) => labels.indexOf(label) !== index));

  labels.forEach((label, index) => {
    if (!duplicateLabels.has(label)) return;
    const year = ranges[index]?.start?.year;
    if (year) labels[index] = `${label} ${year}`;
  });

  return Object.fromEntries(data.series.map((series, index) => [series.rangeId, labels[index] ?? series.label]));
};

/** Index of the primary period containing today, clamped to the completed range for historical reports. */
export const getComparisonActivePeriodIndex = (data: ComparisonOverTimeData, now: DateTime): number => {
  if (data.periods.length === 0) return -1;
  const dated = data.periods.map((period, index) => ({
    end: validDate(period.endOn ?? period.startOn),
    index,
    start: validDate(period.startOn ?? period.endOn),
  }));
  if (dated.some(({ start, end }) => !start || !end)) return data.periods.length - 1;

  const day = now.startOf('day');
  const containing = dated.find(
    ({ start, end }) => start && end && day >= start.startOf('day') && day <= end.endOf('day'),
  );
  if (containing) return containing.index;

  const firstStart = dated[0]?.start;
  if (firstStart && day < firstStart.startOf('day')) return -1;
  const precedingPeriods = dated.filter(({ start }) => start && start.startOf('day') <= day);
  const preceding = precedingPeriods[precedingPeriods.length - 1];
  return preceding?.index ?? data.periods.length - 1;
};

/** The current series stops at today; comparison series keep their complete reference range. */
export const getVisibleComparisonSeriesValues = (
  data: ComparisonOverTimeData,
  series: ComparisonOverTimeSeries,
  now: DateTime,
): Array<number | null> => {
  if (!series.isPrimary) return [...series.values];
  const activeIndex = getComparisonActivePeriodIndex(data, now);
  return series.values.map((value, index) => (index <= activeIndex ? value : null));
};

/** Actual paired bucket names for tooltips when the axis uses relative elapsed labels. */
export const getComparisonPeriodDisplayLabel = (period: ComparisonPeriod | undefined): string => {
  if (!period) return '';
  const primaryLabel = period.label || period.id;
  const comparisonLabel = period.comparisonLabel?.trim();
  return comparisonLabel && comparisonLabel !== primaryLabel ? `${primaryLabel} vs ${comparisonLabel}` : primaryLabel;
};

/** The dated bucket represented by a running value, including a carried value's last real bucket. */
export const getComparisonPointLabel = (
  data: ComparisonOverTimeData,
  periodIndex: number,
  isPrimary: boolean,
  fallback = '',
): string => {
  const hasDatedBuckets = data.periods.some((period) =>
    isPrimary ? Boolean(period.startOn || period.endOn) : Boolean(period.comparisonStartOn || period.comparisonEndOn),
  );
  let datedIndex = periodIndex;
  while (datedIndex >= 0) {
    const candidate = data.periods[datedIndex];
    const hasDate = isPrimary
      ? candidate?.startOn || candidate?.endOn
      : candidate?.comparisonStartOn || candidate?.comparisonEndOn;
    const hasLabel = isPrimary ? candidate?.label : candidate?.comparisonLabel;
    if (hasDate || (!hasDatedBuckets && hasLabel)) break;
    datedIndex -= 1;
  }
  const period = data.periods[datedIndex];
  if (!period) return isPrimary ? fallback || 'date unavailable' : 'date unavailable';
  const startOn = isPrimary ? period.startOn : period.comparisonStartOn;
  const endOn = isPrimary ? period.endOn : period.comparisonEndOn;
  const label = (isPrimary ? period.label : period.comparisonLabel)?.trim() || (isPrimary ? fallback : '');
  const start = DateTime.fromISO(startOn || endOn || '');
  const carried = datedIndex < periodIndex ? 'through ' : '';
  if (!start.isValid || /\b\d{4}\b/.test(label)) return `${carried}${label || period.id}`;

  if (data.timestep === 'DAY') return `${carried}${start.toFormat('LLLL d, yyyy')}`;
  if (data.timestep === 'MONTH') return `${carried}${start.toFormat('LLLL yyyy')}`;
  if (data.timestep === 'QUARTER') return `${carried}Q${start.quarter} ${start.year}`;
  if (data.timestep === 'YEAR') return `${carried}${start.year}`;
  if (data.timestep === 'WEEK') {
    const end = DateTime.fromISO(endOn || startOn || '');
    if (end.isValid) {
      return `${carried}${
        start.year === end.year
          ? `${start.toFormat('LLL d')} – ${end.toFormat('LLL d, yyyy')}`
          : `${start.toFormat('LLL d, yyyy')} – ${end.toFormat('LLL d, yyyy')}`
      }`;
    }
  }
  return `${carried}${label ? `${label} ${start.year}` : start.toFormat('LLLL d, yyyy')}`;
};

/**
 * Whether a comparison report renders the over-time views. A single bucket covers the same span as
 * the whole-range total, so it renders as a Total comparison rather than a one-point trend.
 */
export const isComparisonOverTime = (
  timestep: Timestep | null | undefined,
  periods: readonly ComparisonPeriod[],
): boolean => timestep != null && timestep !== 'NONE' && periods.length > 1;

/** Keeps a selected server period when it still exists, otherwise selects the latest available period. */
export const resolveComparisonPeriodId = (
  periods: readonly ComparisonPeriod[],
  requestedId: string | undefined,
): string | undefined =>
  (requestedId && periods.some((period) => period.id === requestedId) ? requestedId : undefined) ?? periods.at(-1)?.id;

const relativeLabels = (
  count: number,
  unit: 'Day' | 'Week' | 'Month' | 'Quarter' | 'Year',
  sparse: boolean,
): string[] => {
  const stride = !sparse ? 1 : unit === 'Day' ? 7 : Math.max(1, Math.ceil(count / 6));
  return Array.from({ length: count }, (_value, index) => (index % stride === 0 ? `${unit} ${index + 1}` : ''));
};

const elapsedMonthLabels = (count: number): string[] =>
  Array.from({ length: count }, (_value, index) => `Month ${index + 1}`);

/**
 * Axis labels for aligned comparison periods; year-spanning months use elapsed positions.
 * Relative labels (`Day 8`) are thinned for the axis unless `sparse` is false.
 */
export const getComparisonXAxisLabels = (
  data: ComparisonOverTimeData,
  { sparse = true }: { sparse?: boolean } = {},
): string[] => {
  const dates = data.periods.map((period) => validDate(period.startOn));
  if (!dates.every((date): date is DateTime => Boolean(date))) {
    return data.periods.map((period) => period.label || period.id);
  }
  if (dates.length === 1) return data.periods.map((period) => period.label || period.id);
  const comparisonDates = data.periods.map((period) => validDate(period.comparisonStartOn));
  const hasComparisonDates = comparisonDates.every((date): date is DateTime => Boolean(date));
  const lastDate = validDate(data.periods.at(-1)?.endOn) ?? dates.at(-1);
  const spansMultipleYears = dates[0]?.year !== lastDate?.year;

  if (data.timestep === 'DAY') {
    return relativeLabels(dates.length, 'Day', sparse);
  }
  if (data.timestep === 'WEEK') {
    return relativeLabels(dates.length, 'Week', sparse);
  }
  if (data.timestep === 'MONTH') {
    if (spansMultipleYears) return elapsedMonthLabels(dates.length);
    const monthsAlign =
      !hasComparisonDates || dates.every((date, index) => date.month === comparisonDates[index]?.month);
    return monthsAlign ? dates.map((date) => date.toFormat('LLL')) : relativeLabels(dates.length, 'Month', sparse);
  }
  if (data.timestep === 'QUARTER') {
    const quartersAlign =
      !hasComparisonDates || dates.every((date, index) => date.quarter === comparisonDates[index]?.quarter);
    return quartersAlign
      ? dates.map((date) => `Q${date.quarter}${spansMultipleYears ? ` ${date.toFormat('yy')}` : ''}`)
      : relativeLabels(dates.length, 'Quarter', sparse);
  }
  if (data.timestep === 'YEAR') {
    const yearsAlign = !hasComparisonDates || dates.every((date, index) => date.year === comparisonDates[index]?.year);
    return yearsAlign ? dates.map((date) => date.toFormat('yyyy')) : relativeLabels(dates.length, 'Year', sparse);
  }

  // Legacy/generated fixtures may omit timestep. Infer from the median adjacent gap so a partial
  // first bucket cannot make a monthly report look weekly.
  const gaps = dates
    .slice(1)
    .map((date, index) => Math.abs(date.diff(dates[index]!, 'days').days))
    .sort((left, right) => left - right);
  const intervalDays = gaps[Math.floor(gaps.length / 2)] ?? Number.POSITIVE_INFINITY;
  if (intervalDays <= 2) return relativeLabels(dates.length, 'Day', sparse);
  if (intervalDays <= 10) return relativeLabels(dates.length, 'Week', sparse);
  if (intervalDays <= 45) {
    if (spansMultipleYears) return elapsedMonthLabels(dates.length);
    const monthsAlign =
      !hasComparisonDates || dates.every((date, index) => date.month === comparisonDates[index]?.month);
    return monthsAlign ? dates.map((date) => date.toFormat('LLL')) : relativeLabels(dates.length, 'Month', sparse);
  }
  if (intervalDays <= 120) {
    const quartersAlign =
      !hasComparisonDates || dates.every((date, index) => date.quarter === comparisonDates[index]?.quarter);
    return quartersAlign
      ? dates.map((date) => `Q${date.quarter}${spansMultipleYears ? ` ${date.toFormat('yy')}` : ''}`)
      : relativeLabels(dates.length, 'Quarter', sparse);
  }
  return dates.map((date) => date.toFormat('yyyy'));
};

/** A label for every period, for tooltips that name the hovered period even where the axis is thinned. */
export const getComparisonPeriodLabels = (data: ComparisonOverTimeData): string[] =>
  getComparisonXAxisLabels(data, { sparse: false });

const getPrimaryValueColumn = (columns: ReportColumn[]): ReportColumn | undefined =>
  columns.find(
    (column) => (column.rangeId ?? PRIMARY_RANGE_ID) === PRIMARY_RANGE_ID && isChartableComparisonColumn(column),
  ) ?? columns.find(isChartableComparisonColumn);

const getPeriod = (
  groupId: string,
  groupLabel: string | undefined,
  ranges: ComparisonChartRange[],
): ComparisonPeriod => {
  const primaryRange = ranges.find((range) => range.isPrimary);
  const comparisonRange = ranges.find((range) => !range.isPrimary);

  return {
    id: groupId,
    label: primaryRange?.label || groupLabel || '',
    startOn: primaryRange?.startOn,
    endOn: primaryRange?.endOn,
    comparisonLabel: comparisonRange?.label,
    comparisonStartOn: comparisonRange?.startOn,
    comparisonEndOn: comparisonRange?.endOn,
  };
};

const getSeriesLabel = (
  column: ReportColumn | undefined,
  rangeId: string,
  isPrimary: boolean,
  rangeHeader: ComparisonRangeHeader | undefined,
): string => {
  const spanLabel = formatSpan(
    rangeHeader?.startOn && rangeHeader.endOn ? { startOn: rangeHeader.startOn, endOn: rangeHeader.endOn } : undefined,
  );
  if (spanLabel) return spanLabel;

  const rangeLabel = column?.rangeLabel?.trim();
  if (rangeLabel && rangeLabel.toLowerCase() !== 'primary') return rangeLabel;
  if (isPrimary) return 'Current';
  return rangeLabel || rangeId;
};

const getMetricValues = (
  chart: ComparisonPeriodTotals,
  reportSubType: string | undefined,
  cumulative = false,
): Array<number | null> => {
  const sectionKey = reportSubType === 'INCOME' ? 'INCOME' : reportSubType === 'SPENDING' ? 'EXPENSE' : undefined;
  const sectionTotals = cumulative ? chart.cumulativeSectionTotals : chart.sectionTotals;
  const totals = cumulative ? chart.cumulativeTotals : chart.totals;
  const values =
    (sectionKey ? sectionTotals?.find((section) => section.key === sectionKey)?.values : undefined) ??
    totals?.values ??
    chart.ranges.map(() => null);

  // QCS represents expenses as negative accounting values. A spending chart describes amount
  // spent, so orient that metric above zero while allowing net income itself to cross the axis.
  return reportSubType === 'SPENDING' ? values.map((value) => (value == null ? null : -value)) : values;
};

/**
 * Builds the full cumulative comparison view from every aligned PERIOD group.
 *
 * Running totals come from the server's CUMULATIVE_VALUE column cells; the client never sums them.
 * A period without a cumulative cell carries the previous total; it does not reset to zero.
 */
export const toComparisonOverTimeData = (report: Report | null | undefined): ComparisonOverTimeData => {
  if (!report || !isComparisonReport(report)) return EMPTY_COMPARISON_OVER_TIME_DATA;

  const groups = getPeriodColumnGroups(report);
  if (groups.length === 0) return { ...EMPTY_COMPARISON_OVER_TIME_DATA, isComparison: true };

  const reportSubType = report.reportSubType ?? report.configuration?.reportSubType;
  const rangeHeaders = getComparisonRangeHeaders(report);
  const periodCharts = groups.map((group) => {
    const chart = toComparisonPeriodTotals(report, group.groupId);
    return {
      group,
      chart,
      periodMetricValues: getMetricValues(chart, reportSubType),
      cumulativeMetricValues: getMetricValues(chart, reportSubType, true),
    };
  });
  const rangesById = new Map<string, ComparisonChartRange>();
  periodCharts.forEach(({ chart }) => {
    chart.ranges.forEach((range) => {
      if (!rangesById.has(range.rangeId)) rangesById.set(range.rangeId, range);
    });
  });
  const seriesRanges = [...rangesById.values()].sort((left, right) => Number(right.isPrimary) - Number(left.isPrimary));
  if (seriesRanges.length === 0) return { ...EMPTY_COMPARISON_OVER_TIME_DATA, isComparison: true };

  const running = new Map<string, number>();
  const periodValuesByRange = new Map<string, Array<number | null>>(seriesRanges.map((range) => [range.rangeId, []]));
  const valuesByRange = new Map<string, Array<number | null>>(seriesRanges.map((range) => [range.rangeId, []]));

  periodCharts.forEach(({ chart, periodMetricValues, cumulativeMetricValues }) => {
    seriesRanges.forEach((range) => {
      const rangeIndex = chart.ranges.findIndex((candidate) => candidate.rangeId === range.rangeId);
      const periodValue = rangeIndex < 0 ? null : (periodMetricValues[rangeIndex] ?? null);
      const serverCumulative = rangeIndex < 0 ? null : (cumulativeMetricValues[rangeIndex] ?? null);
      const previousTotal = running.get(range.rangeId);
      // Some QCS responses omit the raw period value while still supplying an authoritative
      // cumulative value. The difference from the previous total is that period's value; for the
      // first period, the cumulative value is the period value itself.
      let resolvedPeriodValue = periodValue;
      if (resolvedPeriodValue == null && serverCumulative != null) {
        resolvedPeriodValue = serverCumulative - (previousTotal ?? 0);
      }
      periodValuesByRange.get(range.rangeId)?.push(resolvedPeriodValue);
      if (serverCumulative != null) running.set(range.rangeId, serverCumulative);
      valuesByRange.get(range.rangeId)?.push(running.get(range.rangeId) ?? null);
    });
  });

  const toSeries = (values: Map<string, Array<number | null>>): ComparisonOverTimeSeries[] =>
    seriesRanges.map((range) => {
      const sourceColumn = getPrimaryValueColumn(
        (report.columns ?? []).filter((column) => (column.rangeId ?? PRIMARY_RANGE_ID) === range.rangeId),
      );
      return {
        rangeId: range.rangeId,
        label: getSeriesLabel(sourceColumn, range.rangeId, range.isPrimary, rangeHeaders.get(range.rangeId)),
        isPrimary: range.isPrimary,
        values: values.get(range.rangeId) ?? [],
      };
    });

  return {
    isComparison: true,
    ...(report.configuration?.timestep ? { timestep: report.configuration.timestep } : {}),
    periods: periodCharts.map(({ group, chart }) => getPeriod(group.groupId, group.label, chart.ranges)),
    periodSeries: toSeries(periodValuesByRange),
    // A period column names only its own bucket. The legend describes the complete plotted series,
    // so both views use the union of every period's dates for this range instead.
    series: toSeries(valuesByRange),
  };
};
